import { Popover, Button, Col, Row, Typography, Tabs } from 'antd'
import React, { useEffect, useState } from 'react'
import './index.css'
import { Link } from 'react-router-dom'
import PasswordIco from '../../../assets/Images/LockKeyBlack.svg'
import User from '../../../assets/Images/UserProfileblack.svg'
import DeactivateIco from '../../../assets/Images/deactivate.svg'
import {
  useDeactivateAdminAccount,
  useGetAllAdmins,
  useReactivateAdminAccount,
} from 'api/Admin'
import Spinner from 'components/Spinner'
import { Check } from '@phosphor-icons/react'
import AlertModal from 'helper/AlertModal'
import { adminSettingStore } from 'state/setting/admin'
import ChangePasswordModal from './Modals/ChangePasswordModal'
import AddAdmin from './Modals/AddAdmin'
import MailNotFoundModal from './Modals/MailNotFoundModal'
import MemberFound from './Modals/MemberFound'
import CustomAvatar from 'components/global/CustomAvatar'
import { AdminResp } from 'types/settings/general'

const { Title } = Typography

const AdminsMain: React.FC = () => {
  const [userId, setId] = useState<any>(null)
  const [email, setEmail] = useState<string>()
  const [alertModal, setAlertModal] = useState<boolean>(false)
  const [params, setParams] = useState({ isBlocked: false })
  const {
    changePasswordModal,
    setChangePasswordModal,
    addAdminModal,
    setAddAdminModal,
    mailNotFoundModal,
    mailFoundModal,
  } = adminSettingStore(store => ({
    changePasswordModal: store.changePasswordModal,
    setChangePasswordModal: store.setChangePasswordModal,
    addAdminModal: store.addAdminModal,
    setAddAdminModal: store.setAddAdminModal,
    mailNotFoundModal: store.mailNotFoundModal,
    mailFoundModal: store.mailFoundModal,
  }))

  const {
    isLoading: adminListLoading,
    isFetching: adminListFetching,
    refetch: adminListRefetch,
    isError: adminListError,
    data: adminsList,
  } = useGetAllAdmins(params)

  const { mutate: reactivateAccount } = useReactivateAdminAccount()
  const { mutate: adminDeactivate, isLoading: adminDeactivateLoading } =
    useDeactivateAdminAccount()

  const handleActivateAccount = (email: string) => {
    reactivateAccount({ email })
  }

  const handleYesDelete = () => {
    const payload = { email: email }
    adminDeactivate(payload, {
      onSuccess: () => setAlertModal(false),
    })
  }

  useEffect(() => {
    adminListRefetch()
  }, [params])

  const buttonContent = (
    uid: string,
    firebaseId: string,
    email?: string,
    isBlocked?: boolean,
  ) => (
    <div className="cursor_pointer m-10">
      <div className="d-flex w-100">
        <img alt="" src={User} />
        <Link to={{ pathname: `/admin-profile/${uid}` }}>
          <p className="font15 ant-form flex pl-10">View profile</p>
        </Link>
      </div>
      <div className="d-flex w-100 mt-12">
        <img alt="" src={PasswordIco} />
        <p
          className="font15 ant-form flex pl-10"
          onClick={() => {
            setId(firebaseId)
            setChangePasswordModal(true)
          }}
        >
          Change password
        </p>
      </div>
      {isBlocked ? (
        <div className="d-flex w-100 mt-12">
          <Check size={20} color="#000" />
          <p
            className="font15 ant-form flex pl-10"
            onClick={() => handleActivateAccount(email!)}
          >
            Activate account
          </p>
        </div>
      ) : (
        <div className="d-flex w-100 mt-12">
          <img alt="" src={DeactivateIco} />
          <p
            className="font15 ant-form flex pl-10"
            style={{ color: '#FF6961' }}
            onClick={() => {
              setEmail(email)
              setAlertModal(true)
            }}
          >
            Deactivate account
          </p>
        </div>
      )}
    </div>
  )

  // Extracted component for a single admin card
  const AdminCard = ({ data }: { data: any }) => (
    <div
      className="d-flex justify-between align-items-start mb-25 flex-wrap bb-solid-1 b-color-gray"
      key={data.id}
    >
      <div className="pb-10">
        <Row>
          <Col span={24}>
            <div className="flex align-center gap-16px mb-12">
              <CustomAvatar
                src={data.photo?.post}
                size={40}
                name={`${data.firstName} ${data.lastName}`}
              />
              <div>
                <h5 className="b1">
                  {data.firstName} {data.lastName}
                </h5>
                <span className="text-gray-color d-block font13">
                  {data.email}
                </span>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <div className="pb-20 cursor_pointer">
        <Popover
          content={buttonContent(
            data.id,
            data.firebaseId,
            data.email,
            data.additionalEmails[0].isBlocked,
          )}
          placement="bottom"
        >
          <Button className="br-10 px-30 py-8" type="primary">
            Manage
          </Button>
        </Popover>
      </div>
    </div>
  )

  if (adminListError) {
    return <div>Something went wrong while loading admins</div>
  }

  const renderAdmins = () =>
    adminListLoading || adminListFetching ? (
      <div className="py-40px flex justify-center items-center">
        <Spinner />
      </div>
    ) : (
      adminsList.map((data: AdminResp) => (
        <AdminCard key={data._id} data={data} />
      ))
    )

  return (
    <div className="profile_container" style={{ marginTop: 60 }}>
      <div className="d-flex justify-between align-items-center mb-25">
        <Title level={4} style={{ marginBottom: '0px' }}>
          Admins
        </Title>

        <Button
          className="br-12"
          type="primary"
          onClick={() => setAddAdminModal(true)}
        >
          Add Admin
        </Button>
      </div>

      <Tabs onChange={tab => setParams({ isBlocked: tab === 'deactivated' })}>
        <Tabs.TabPane tab="Active" key="active">
          <div className="mt-40px">{renderAdmins()}</div>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Deactivated" key="deactivated">
          <div className="mt-40px">{renderAdmins()}</div>
        </Tabs.TabPane>
      </Tabs>

      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            name: 'Deactivate Admin',
            heading: 'Deactivate Admin',
            message1: 'Are you sure you want to deactivate this admin?',
            okText: 'Yes, Deactivate',
          }}
          onYesClick={handleYesDelete}
          loading={adminDeactivateLoading}
        />
      )}
      {changePasswordModal && <ChangePasswordModal firebaseId={userId} />}
      {addAdminModal && <AddAdmin />}
      {mailNotFoundModal && <MailNotFoundModal />}
      {mailFoundModal && <MemberFound />}
    </div>
  )
}

export default AdminsMain
